import React, { useState, useEffect, useReducer, useRef, useContext } from "react";

import clsx from "clsx";
import {
  renderDailyTimestamps as renderDailyTimestampsUtil,
  renderNumberTicket as renderNumberTicketUtil,
  renderMessageDivider as renderMessageDividerUtil,
} from "./components/SystemMessageDivider";
import { useTimezone } from "../../hooks/useTimezone";

import useMessagesListStyles from "./messagesListStyles";
import useMessagesListSocket from "./useMessagesListSocket";
import { CircularProgress, useTheme } from "@material-ui/core";

import {
  Notifications,
  SwapHoriz,
  Note,
  Info,
  Person,
} from "@material-ui/icons";

import MessageOptionsMenu from "../MessageOptionsMenu";
import { listMessages } from "../../services/messages.api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { i18n } from "../../translate/i18n";
import MessageItem from "./components/MessageItem";


// ✅ Función para formatear nombre y número en formato "Nombre - +Número"
const formatSenderDisplay = (senderContact, contact, participant) => {
  // Prioridad 1: senderContact (del backend)
  const name = senderContact?.name || contact?.name || null;
  let number = senderContact?.number || contact?.number || 
               (participant ? participant.replace(/@lid|@s\.whatsapp\.net/g, '') : null);
  
  // Limpiar número (solo dígitos) para comparación
  const cleanNumber = number ? number.replace(/\D/g, '') : null;
  const cleanName = name ? name.replace(/\D/g, '') : null;
  
  // Si tenemos nombre Y número, mostrar "Nombre | +Número"
  if (name && number && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    // Formatear número con prefijo + (agregar solo si no lo tiene)
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return `${name} | ${formattedNumber}`;
  }
  
  // Si solo tenemos nombre (y no es un número), mostrar solo nombre
  if (name && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    return name;
  }
  
  // Si solo tenemos número, mostrar "+Número"
  if (number) {
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return formattedNumber;
  }
  
  // Fallback
  return 'Desconocido';
};

// ✅ Función para generar color único por remitente (similar a WhatsApp Web)
const getSenderColor = (identifier) => {
  // Paleta de colores de WhatsApp Web para remitentes en grupos
  const colors = [
    '#00a884', // Verde WhatsApp
    '#0da9e0', // Azul claro
    '#9c27b0', // Púrpura
    '#e91e63', // Rosa
    '#ff9800', // Naranja
    '#795548', // Marrón
    '#607d8b', // Gris azulado
    '#f44336', // Rojo
    '#4caf50', // Verde claro
    '#2196f3', // Azul
    '#ff5722', // Naranja oscuro
    '#009688', // Verde azulado
    '#673ab7', // Púrpura oscuro
  ];
  
  // Generar hash simple del identificador (número o ID)
  let hash = 0;
  const str = String(identifier || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Usar el hash para seleccionar un color de la paleta
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Pasa createdAt sin modificar; formatTime convierte UTC a TZ empresa en el render
const normalizeSocketMessageCreatedAt = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return raw;
  if (raw instanceof Date) return raw.toISOString();
  return null;
};

const MessagesList = ({ ticket, ticketId, isGroup, readOnlyPreview = false }) => {
  const classes = useMessagesListStyles();
  const theme = useTheme();
  const { formatTime, formatDateTime, parseNaiveDate } = useTimezone(); // ✅ Usar hook de timezone

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const isMounted = useRef(true);
  const socketManager = useContext(SocketContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  // ✅ CORRECCIÓN: Extraer ticketId del ticket para evitar expresión compleja en dependencias
  const ticketIdFromTicket = ticket?.id;
  
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    // ✅ CORRECCIÓN: Actualizar ref con ticketId o ticket.id (priorizar ticketId prop)
    const effectiveTicketId = ticketId || ticketIdFromTicket;
    currentTicketId.current = effectiveTicketId;
    
    // console.log("[MessagesList] TicketId actualizado:", {
    //   ticketId,
    //   ticketIdFromTicket,
    //   effectiveTicketId,
    //   currentTicketIdRef: currentTicketId.current
    // });
    
    // ✅ REMOVIDO: El scroll se hará después de cargar los mensajes
  }, [ticketId, ticketIdFromTicket]);

  useEffect(() => {
    // 0297 Fase A: sin demora artificial previa a GET /messages (antes 500 ms sumaba ~1 s con Ticket).
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await listMessages(ticketId, { pageNumber });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          // ✅ MEJORADO: Scroll al final solo cuando se carga la primera página
          if (pageNumber === 1) {
            // Post-render scroll (0297 A.3): valor bajo; no afecta al fetch inicial
            setTimeout(() => {
              scrollToBottom();
            }, 120);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 0);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  // ✅ AGREGADO: Scroll al final cuando se actualicen los mensajes
  useEffect(() => {
    if (messagesList.length > 0 && pageNumber === 1) {
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    }
  }, [messagesList.length, pageNumber]);

  const scrollToBottom = () => {
    // ✅ MEJORADO: Scroll más robusto al final de los mensajes
    const messagesContainer = document.getElementById("messagesList");
    if (!messagesContainer) return;
    
    // ✅ MÉTODO 1: Usar scrollIntoView si hay ref
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ 
        behavior: "smooth", 
        block: "end",
        inline: "nearest"
      });
    } else {
      // ✅ MÉTODO 2: Scroll directo al final del contenedor
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth"
      });
    }
    
    // ✅ MÉTODO 3: Fallback adicional para asegurar scroll al final
    setTimeout(() => {
      if (messagesContainer.scrollTop < messagesContainer.scrollHeight - messagesContainer.clientHeight - 50) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 200);
  };

  useMessagesListSocket({
    socketManager,
    ticketId,
    ticketIdFromTicket,
    isMounted,
    currentTicketId,
    dispatch,
    parseNaiveDate,
    scrollToBottom,
  });

  // ✅ FIX 05/12/2025: UN SOLO useEffect controla isMounted cuando el componente se monta/desmonta
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Sin dependencias = solo al montar/desmontar el componente

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const hanldeReplyMessage = (e, message) => {
    if (readOnlyPreview) return;
    //if (ticket.status === "open" || ticket.status === "group") {
    setAnchorEl(null);
    setReplyingMessage(message);
    //}
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    if (readOnlyPreview) return;
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };


  const renderDailyTimestamps = (message, index) =>
    renderDailyTimestampsUtil(message, index, messagesList, parseNaiveDate, classes);
  const renderNumberTicket = (message, index, filteredMessages) =>
    renderNumberTicketUtil(message, index, filteredMessages, formatDateTime, classes);
  const renderMessageDivider = (message, index) =>
    renderMessageDividerUtil(message, index, messagesList);


  const renderMessages = () => {
    if (messagesList.length > 0) {
      // ✅ FILTRAR MENSAJES DE REACCIÓN Y MENSAJES VACÍOS
      const filteredMessages = messagesList.filter(message => {
        // ✅ EXCLUIR MENSAJES DE REACCIÓN
        if (message.mediaType === "reactionMessage") {
          return false;
        }
        // ✅ PERMITIR MENSAJES INTERNOS, ALERTAS DE SISTEMA Y LLAMADAS (siempre se muestran)
        if (message.mediaType?.startsWith("internal_") || 
            message.mediaType === "system-alert" || 
            message.mediaType === "call_log") {
          return true;
        }
        // ✅ EXCLUIR MENSAJES VACÍOS (sin body y sin mediaUrl)
        if (!message.body && !message.mediaUrl) {
          return false;
        }
        return true;
      });
      
      const viewMessagesList = filteredMessages.map((message, index) => {

        // ✅ NUEVO (31/12/2025): RENDERIZAR MENSAJES DE ALERTA DE SISTEMA (system-alert)
        if (message.mediaType === "system-alert") {
          // Dividir el mensaje en líneas para formatearlo
          const alertLines = message.body?.split('\n').filter(line => line.trim() !== '') || [];
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                margin: '12px 0',
                padding: '12px 0'
              }}>
                {/* ✅ LÍNEA DIVISORIA */}
                <div style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                  marginBottom: '12px'
                }} />
                
                {/* ✅ BANNER DE ALERTA AMARILLO/NARANJA */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fef3c7', // Amarillo/naranja oscuro en dark, claro en light
                  border: `1px solid ${theme.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
                  borderRadius: '8px',
                  padding: '12px 20px',
                  maxWidth: '600px',
                  width: '100%',
                  color: theme.mode === 'dark' ? '#fbbf24' : '#92400e',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {/* ✅ ICONO DE ALERTA */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                    fontWeight: 600
                  }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    {alertLines.length > 0 && (
                      <span>{alertLines[0]}</span>
                    )}
                  </div>
                  
                  {/* ✅ LÍNEAS ADICIONALES DEL MENSAJE */}
                  {alertLines.length > 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      width: '100%',
                      marginTop: '8px'
                    }}>
                      {alertLines.slice(1).map((line, idx) => (
                        <div key={idx} style={{
                          textAlign: 'left',
                          fontWeight: idx === alertLines.length - 2 ? 500 : 400, // Última línea (timestamp) más clara
                          opacity: idx === alertLines.length - 2 ? 0.8 : 1,
                          fontSize: idx === alertLines.length - 2 ? '12px' : '13px'
                        }}>
                          {line.trim()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }

        // ✅ RENDERIZAR MENSAJES INTERNOS (toast, transfer, note, action)
        if (message.mediaType?.startsWith("internal_")) {
          const internalType = message.mediaType.replace("internal_", "");
          let icon, className;
          
          switch (internalType) {
            case "toast":
              icon = <Notifications className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalToast);
              break;
            case "transfer":
              icon = <SwapHoriz className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalTransfer);
              break;
            case "note":
              icon = <Note className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalNote);
              break;
            case "action":
              icon = <Info className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "signature":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "delete":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            default:
              icon = null;
              className = classes.messageInternal;
          }

          // ✅ DETECTAR SI ES MENSAJE DE TRANSFERENCIA O DEVOLUCIÓN PARA FORMATO ESPECIAL
          const isTransferMessage = internalType === "action" && (
            message.body?.includes("transfirió") || 
            message.body?.includes("devuelta") || 
            message.body?.includes("asignada") ||
            message.body?.includes("cambió de")
          );
          
          // ✅ DEBUG: Log temporal removido para evitar spam en consola
          // if (isTransferMessage && process.env.NODE_ENV === 'development') {
          //   console.log('🔍 Transfer message detected:', {
          //     body: message.body,
          //     bodyLength: message.body?.length,
          //     hasNewlines: message.body?.includes('\n'),
          //     lines: message.body?.split(/\r?\n/),
          //     mediaType: message.mediaType
          //   });
          // }

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              {isTransferMessage ? (
                // ✅ FORMATO ESPECIAL PARA TRANSFERENCIAS Y DEVOLUCIONES - UNA SOLA LÍNEA DIVISORIA
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  margin: '12px 0',
                  padding: '12px 0'
                }}>
                  {/* ✅ UNA SOLA LÍNEA DIVISORIA */}
                  <div style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                    marginBottom: '12px'
                  }} />
                  
                  {/* ✅ CONTENIDO CENTRADO CON ICONO ENTRE LÍNEAS */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: theme.mode === 'dark' ? '#d1d5db' : '#666',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    padding: '0 20px',
                    position: 'relative'
                  }}>
                    {/* ✅ DIVIDIR EL TEXTO EN LÍNEAS Y COLOCAR ICONO ENTRE ELLAS */}
                    {(() => {
                      // Limpiar el texto y dividir por saltos de línea
                      const cleanBody = message.body?.trim() || '';
                      const lines = cleanBody.split(/\r?\n/).filter(line => line.trim() !== '');
                      
                      // ✅ DEBUG: Log removido para evitar spam en consola
                      // if (process.env.NODE_ENV === 'development') {
                      //   console.log('🔍 Transfer message lines:', lines.length, lines);
                      // }
                      
                      // Si tiene 3 o más líneas, usar formato estructurado
                      if (lines.length >= 3) {
                        return (
                          <>
                            {/* Línea 1: "Se transfirió la conversación de usuarios" */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400
                            }}>
                              {lines[0].trim()}
                            </div>
                            
                            {/* Línea 2: Icono y nombre del usuario en la misma línea */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}>
                              {icon && (
                                <span style={{ 
                                  opacity: 0.8,
                                  color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {icon}
                                </span>
                              )}
                              <span>{lines[1].trim()}</span>
                            </div>
                            
                            {/* Línea 3: Fecha y hora */}
                            <div style={{
                              fontWeight: 400,
                              fontSize: '12px',
                              opacity: 0.8
                            }}>
                              {lines[2].trim()}
                            </div>
                          </>
                        );
                      } else if (lines.length === 2) {
                        // Si tiene 2 líneas, mostrar con icono en medio
                        return (
                          <>
                            <div style={{ marginBottom: '4px' }}>
                              {lines[0].trim()}
                            </div>
                            {icon && (
                              <div style={{ 
                                margin: '6px 0',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div>
                              {lines[1].trim()}
                            </div>
                          </>
                        );
                      } else {
                        // Fallback: mostrar mensaje completo con icono arriba
                        return (
                          <>
                            {icon && (
                              <div style={{ 
                                marginBottom: '8px',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {cleanBody}
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                // ✅ FORMATO NORMAL PARA OTROS MENSAJES INTERNOS
                // ✅ NUEVO: Si es internal_signature o internal_delete, alinearlo a la derecha
                (internalType === "signature" || internalType === "delete") ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginRight: 20,
                    marginTop: 2,
                    marginBottom: 2
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                      fontSize: '13px',
                      padding: '4px 12px',
                      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6',
                      borderRadius: '8px'
                    }}>
                      {icon}
                      <span>{message.body}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {formatTime(message.createdAt || new Date())}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={className}>
                    {icon}
                    <span>{message.body}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>
                      {formatTime(message.createdAt || new Date())}
                    </span>
                  </div>
                )
              )}
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          // ✅ DETECTAR SI ES LLAMADA ACEPTADA O PERDIDA
          const isAcceptedCall = message.body && message.body.toLowerCase().includes("aceptada");
          const iconColor = isAcceptedCall ? "#4caf50" : "#df3333"; // Verde para aceptada, rojo para perdida
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                {isGroup && (
                  <span 
                    className={classes.messageContactName}
                    style={{ 
                      color: getSenderColor(
                        message.senderContact?.number || 
                        message.senderContact?.id || 
                        message.contact?.number ||
                        message.participant
                      )
                    }}
                  >
                    {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
                  </span>
                )}
                <div>
                  {isAcceptedCall ? (
                    // ✅ ICONO PARA LLAMADA ACEPTADA: Teléfono normal (verde) - sin X
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1z"/>
                    </svg>
                  ) : (
                    // ✅ ICONO PARA LLAMADA PERDIDA: Teléfono con X (rojo)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                    </svg>
                  )}
                  <span>{message.body || (message.createdAt && !isNaN(new Date(message.createdAt).getTime()) ? `Llamada de voz/vídeo perdida a las ${formatTime(message.createdAt)}` : "Llamada de voz/vídeo perdida")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderNumberTicket(message, index, filteredMessages)}
            {renderMessageDivider(message, index)}
            <MessageItem
              message={message}
              index={index}
              filteredMessages={filteredMessages}
              lastMessageRef={lastMessageRef}
              classes={classes}
              isGroup={isGroup}
              readOnlyPreview={readOnlyPreview}
              showSelectMessageCheckbox={showSelectMessageCheckbox}
              formatTime={formatTime}
              hanldeReplyMessage={hanldeReplyMessage}
              handleOpenMessageOptionsMenu={handleOpenMessageOptionsMenu}
              getSenderColor={getSenderColor}
              formatSenderDisplay={formatSenderDisplay}
            />
          </React.Fragment>
        );
      });
      return viewMessagesList;
    } else {
      return <div>{i18n.t("hardcodedElements.sayHelloToNewContact")}</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
